import {
  DeleteConfirmation,
  Divider, ErrorMessages, IconButton, IconButtonType, Page, Spinner
} from "@whitespace/whitespace-ui";
import {
  Link, useNavigate, useParams, useLocation
} from "react-router-dom";
import {
  Breadcrumb,
  Button, Card, Col, Container, Form, FormGroup, Row
} from "react-bootstrap";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import authorisedVisitorTemporaryWorkerSchema from "modules/schemas/authorisedVisitorTemporaryWorkerSchema";
import { useAmplifyREST } from "hooks/useAmplifyREST";
import AmplifyError from "components/AmplifyError/AmplifyError";
import { useCallback, useEffect, useState } from "react";
import { ariaInvalidCheck } from "@whitespace/whitespace-utils";
import { TimePickerComponent } from "@syncfusion/ej2-react-calendars";

import { RoutePathsEnum } from "infrastructure/RoutePathsEnum";
import IEditAuthorisedVisitorTemporaryWorkerRequestModel from "interfaces/Request/IEditAuthorisedVisitorTemporaryWorkerRequestModel";

import "./AuthorisedVisitorTemporaryWorker.scss";
import { convertToDateFromLocalDate } from "helpers/DateUtils";

const EditAuthorisedVisitorTemporaryWorker = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const { hwrcSiteId, diaryDate, authorisedVisitorTemporaryWorkerId } = useParams();
  const editDetails = { ...location.state as IEditAuthorisedVisitorTemporaryWorkerRequestModel };
  const returnUrl = `${RoutePathsEnum.SiteDiaryLandingPage}${hwrcSiteId && diaryDate ? `/${hwrcSiteId}/${diaryDate}` : ""}`;
  const [showExceptions, setShowExceptions] = useState<boolean>(true);
  const [updateButtonDisabled, setUpdateButtonDisabled] = useState<boolean>(true);
  const [showTimeOutError, setShowTimeOutError] = useState<boolean>(false);
  const [deleteButtonPressed, setDeleteButtonPressed] = useState<boolean>(false);

  const [timeInRef, setTimeInRef] = useState<TimePickerComponent | undefined>();
  const [timeOutRef, setTimeOutRef] = useState<TimePickerComponent | undefined>();

  const {
    formState: { errors },
    register,
    handleSubmit,
    getValues,
    setValue
  } = useForm<IEditAuthorisedVisitorTemporaryWorkerRequestModel>({
    resolver: yupResolver(authorisedVisitorTemporaryWorkerSchema),
    shouldFocusError: true,
    reValidateMode: "onBlur"
  });

  const {
    isSending: updateAuthorisedVisitorTemporaryWorkerIsSending,
    errors: updateAuthorisedVisitorTemporaryWorkerErrors,
    sendRequest: updateAuthorisedVisitorTemporaryWorkerSendRequest,
    statusCode: updateAuthorisedVisitorTemporaryWorkerStatusCode,
    resetState: updateAuthorisedVisitorTemporaryWorkerResetState
  } = useAmplifyREST();

  const {
    isSending: deleteAuthorisedVisitorTemporaryWorkerIsSending,
    errors: deleteAuthorisedVisitorTemporaryWorkerErrors,
    sendRequest: deleteAuthorisedVisitorTemporaryWorkerSendRequest,
    statusCode: deleteAuthorisedVisitorTemporaryWorkerStatusCode
  } = useAmplifyREST();

  const isSending = updateAuthorisedVisitorTemporaryWorkerIsSending || deleteAuthorisedVisitorTemporaryWorkerIsSending;
  const apiErrors = updateAuthorisedVisitorTemporaryWorkerErrors || deleteAuthorisedVisitorTemporaryWorkerErrors;

  const exceptionAlertCloseHandler = useCallback(() => {
    setShowExceptions(false);
  }, [setShowExceptions]);

  const handleUpdateButtonClick = (request : IEditAuthorisedVisitorTemporaryWorkerRequestModel) => {
    const timeIn = convertToDateFromLocalDate(diaryDate!);
    timeIn.setHours(timeInRef!.value.getHours());
    timeIn.setMinutes(timeInRef!.value.getMinutes());

    let timeOut;

    if (timeOutRef?.value) {
      timeOut = convertToDateFromLocalDate(diaryDate!);
      timeOut.setHours(timeOutRef!.value.getHours());
      timeOut.setMinutes(timeOutRef!.value.getMinutes());
    }

    request.timeIn = timeIn.toISOString();
    request.timeOut = timeOut ? timeOut.toISOString() : undefined;

    updateAuthorisedVisitorTemporaryWorkerResetState();
    updateAuthorisedVisitorTemporaryWorkerSendRequest(encodeURI(`hwrcsitediary/authorisedvisitor/${authorisedVisitorTemporaryWorkerId}`), "PUT", request);
  };

  useEffect(() => {
    if (updateAuthorisedVisitorTemporaryWorkerStatusCode === 200) {
      navigate(returnUrl);
    }
  }, [updateAuthorisedVisitorTemporaryWorkerStatusCode]);

  const handleDeleteButtonClick = () => {
    setDeleteButtonPressed(false);
    deleteAuthorisedVisitorTemporaryWorkerSendRequest(encodeURI(`hwrcsitediary/authorisedvisitor/${authorisedVisitorTemporaryWorkerId}`), "DELETE");
  };

  useEffect(() => {
    if (deleteAuthorisedVisitorTemporaryWorkerStatusCode === 200) {
      navigate(returnUrl);
    }
  }, [deleteAuthorisedVisitorTemporaryWorkerStatusCode]);

  useEffect(() => {
    if (deleteAuthorisedVisitorTemporaryWorkerStatusCode === 401) {
      navigate(RoutePathsEnum.Unauthorised);
    }
  }, [deleteAuthorisedVisitorTemporaryWorkerStatusCode]);

  useEffect(() => {
    if (updateAuthorisedVisitorTemporaryWorkerStatusCode === 401) {
      navigate(RoutePathsEnum.Unauthorised);
    }
  }, [updateAuthorisedVisitorTemporaryWorkerStatusCode]);

  const canFormBeSubmitted = () => {
    const mandatoryFieldsEntered = (getValues("name").length > 0 && getValues("organisation").length > 0 && timeInRef?.value != null);

    let timeInChanged = false;
    let timeOutChanged = false;
    let timeInCalc = convertToDateFromLocalDate(diaryDate!);
    let timeOutCalc = convertToDateFromLocalDate(diaryDate!);

    if (timeInRef?.value) {
      timeInCalc = convertToDateFromLocalDate(diaryDate!);
      timeInCalc.setHours(timeInRef!.value.getHours());
      timeInCalc.setMinutes(timeInRef!.value.getMinutes());
      timeInChanged = timeInCalc.getTime() !== new Date(editDetails.timeIn).getTime();
    }

    if (timeOutRef?.value) {
      timeOutCalc = convertToDateFromLocalDate(diaryDate!);
      timeOutCalc.setHours(timeOutRef!.value.getHours());
      timeOutCalc.setMinutes(timeOutRef!.value.getMinutes());

      if (editDetails.timeOut) {
        timeOutChanged = timeOutCalc.getTime() !== new Date(editDetails.timeOut!).getTime();
      } else {
        timeOutChanged = true;
      }
    } else {
      timeOutChanged = (editDetails.timeOut !== undefined && editDetails.timeOut.length > 0);
    }

    const valueHasChanged = getValues("name") !== editDetails.name
    || getValues("organisation") !== editDetails.organisation
    || getValues("inducted") !== editDetails.inducted
    || timeInChanged
    || timeOutChanged;

    let isTimeOutBeforeTimeIn = false;
    if (timeInRef?.value && timeOutRef?.value) {
      isTimeOutBeforeTimeIn = timeOutCalc <= timeInCalc;
    }

    setUpdateButtonDisabled(!(mandatoryFieldsEntered && valueHasChanged && !isTimeOutBeforeTimeIn));
    setShowTimeOutError(isTimeOutBeforeTimeIn);
  };
  return (
    <Page>
      {showExceptions && apiErrors && (
        <AmplifyError
          errors={apiErrors}
          isDismissable
          onCloseHandler={exceptionAlertCloseHandler}
        />
      )}
      <Divider />
      <Card>
        <Card.Header>
          <h1>
            Edit Visitor Entry
            <Spinner
              animation="grow"
              visible={isSending}
              spinnerText="Loading"
              variant="info"
            />
          </h1>
        </Card.Header>
        <Card.Body className="no-padding">
          <Form
            autoComplete="off"
            onSubmit={handleSubmit(handleUpdateButtonClick)}
          >
            <Row className="d-lg-flex w-100 align-items-center top-margin-small bottom-margin-small">
              <Col xs={8} className="d-flex">
                <Breadcrumb className="breadcrumb-manage-user left-margin-large no-wrap">
                  <Breadcrumb.Item
                    data-testid="breadcrumb-diary"
                    linkAs={Link}
                    linkProps={{ to: returnUrl }}
                    onClick={() => navigate(returnUrl)}
                  >
                    Diary
                  </Breadcrumb.Item>
                  <Breadcrumb.Item active data-testid="breadcrumb-edit-visitor">
                    Edit Visitor
                  </Breadcrumb.Item>
                </Breadcrumb>
              </Col>
              <Col className="d-lg-flex justify-content-end">
                <IconButton
                  iconType={IconButtonType.Delete}
                  variant="danger"
                  className="right-margin-small"
                  aria-disabled={deleteAuthorisedVisitorTemporaryWorkerIsSending}
                  aria-label="Delete Visitor"
                  type="button"
                  spin={deleteAuthorisedVisitorTemporaryWorkerIsSending}
                  onClick={() => setDeleteButtonPressed(true)}
                  data-testid="btnDelete"
                >
                  Delete
                </IconButton>
                <IconButton
                  iconType={IconButtonType.Save}
                  variant="primary"
                  className="right-margin-small"
                  disabled={updateButtonDisabled || updateAuthorisedVisitorTemporaryWorkerIsSending}
                  aria-disabled={updateButtonDisabled || updateAuthorisedVisitorTemporaryWorkerIsSending}
                  type="submit"
                  spin={updateAuthorisedVisitorTemporaryWorkerIsSending}
                  data-testid="btnUpdate"
                  aria-label="update authorised visitor or temporary worker"
                >
                  Update
                </IconButton>
                <Button
                  variant="secondary"
                  onClick={() => navigate(returnUrl)}
                  aria-label="Cancel update and return to site diary"
                  data-testid="cancel"
                >
                  Cancel
                </Button>
              </Col>
            </Row>

            <Divider />
            <section className="authorised-visitor-temporary-worker pt-2">
              <Container fluid>
                <Row>
                  <div className="control-pane">
                    <div className="control-section" />

                    <Row className="mt-3">
                      <FormGroup as={Row} controlId="name">
                        <Form.Label id="nameLabel" className="horiz-form-label">
                          Name:
                        </Form.Label>
                        <Col>
                          <Form.Control
                            type="text"
                            role="textbox"
                            aria-required="true"
                            aria-labelledby="nameLabel"
                            aria-invalid={ariaInvalidCheck(
                              "name",
                              errors,
                              null
                            )}
                            maxLength={50}
                            minLength={1}
                            onKeyUp={() => canFormBeSubmitted()}
                            {...register("name")}
                            defaultValue={editDetails.name}
                          />
                        </Col>
                        <ErrorMessages
                          fieldName="name"
                          errors={errors}
                          validationFailures={null}
                        />
                      </FormGroup>
                    </Row>
                    <Row className="mt-3">
                      <FormGroup as={Row} controlId="organisation">
                        <Form.Label id="organisationLabel" className="horiz-form-label">
                          Organisation:
                        </Form.Label>
                        <Col>
                          <Form.Control
                            type="text"
                            role="textbox"
                            aria-required="true"
                            aria-labelledby="organisationLabel"
                            aria-invalid={ariaInvalidCheck(
                              "organisation",
                              errors,
                              null
                            )}
                            maxLength={50}
                            minLength={1}
                            onKeyUp={() => canFormBeSubmitted()}
                            {...register("organisation")}
                            defaultValue={editDetails.organisation}
                          />
                        </Col>
                        <ErrorMessages
                          fieldName="organisation"
                          errors={errors}
                          validationFailures={null}
                        />
                      </FormGroup>
                    </Row>
                    <Row className="mt-3">
                      <div className="w-auto">
                        <FormGroup as={Row} className="mb-6 d-flex time" controlId="timeIn">
                          <Form.Label id="timeInLabel" className="d-inline-flex horiz-form-label w-auto mb-0 me-3">
                            Time In:
                          </Form.Label>
                          <div className="ml-1 mt-1">
                            <TimePickerComponent
                              ref={(t: TimePickerComponent) => { setTimeInRef(t); }}
                              data-testid="timeIn"
                              id="timeIn"
                              type="text"
                              htmlAttributes={{ "aria-labelledby": "timeInLabel", "aria-required": "true" }}
                              format="HH:mm"
                              value={new Date(editDetails.timeIn!)}
                              change={() => { canFormBeSubmitted(); }}
                              showClearButton={false}
                            />
                          </div>
                        </FormGroup>
                        {showTimeOutError && (
                          <span
                            data-testid="timeInTimeOutError"
                            className="validation-error"
                          >
                            The Time In cannot be greater than the Time Out
                          </span>
                        )}

                      </div>
                    </Row>
                    <Row className="mt-3">
                      <div className="w-auto">
                        <FormGroup as={Row} className="mb-6 d-flex time" controlId="timeOut">
                          <Form.Label id="timeOutLabel" className="d-inline-flex horiz-form-label w-auto mb-0 me-3">
                            Time Out:
                          </Form.Label>
                          <div className="ml-1 mt-1">
                            <TimePickerComponent
                              ref={(t: TimePickerComponent) => { setTimeOutRef(t); }}
                              data-testid="timeOut"
                              id="timeOut"
                              type="text"
                              htmlAttributes={{ "aria-labelledby": "timeOutLabel", "aria-required": "false" }}
                              format="HH:mm"
                              change={() => { canFormBeSubmitted(); }}
                              showClearButton={false}
                              value={editDetails.timeOut ? new Date(editDetails.timeOut) : undefined}
                            />
                          </div>
                        </FormGroup>
                      </div>
                    </Row>
                    <Row className="mb-3 mt-3">
                      <FormGroup
                        as={Row}
                        className="mb-6 d-flex"
                        controlId="active"
                      >
                        <Form.Label
                          id="inductedLabel"
                          className="d-inline-flex horiz-form-label w-auto mb-0 me-3"
                        >
                          Inducted?:
                        </Form.Label>

                        <Form.Check
                          {...register("inducted")}
                          type="switch"
                          className="d-inline-flex custom-switch custom-switch-md ms-2 w-auto"
                          aria-labelledby="inductedLabel"
                          aria-required="true"
                          defaultChecked={editDetails.inducted}
                          onChange={() => { setValue("inducted", !getValues("inducted")); canFormBeSubmitted(); }}
                        />

                        <ErrorMessages
                          fieldName="inducted"
                          errors={errors}
                          validationFailures={null}
                        />
                      </FormGroup>
                    </Row>

                  </div>
                </Row>
              </Container>

            </section>

          </Form>
        </Card.Body>
      </Card>
      <DeleteConfirmation
        title="Delete Visitor"
        onDelete={() => { handleDeleteButtonClick(); }}
        onHide={() => { setDeleteButtonPressed(false); }}
        isDeleting={deleteAuthorisedVisitorTemporaryWorkerIsSending}
        show={deleteButtonPressed}

      >
        <p>Are you sure you want to delete this Visitor?</p>
      </DeleteConfirmation>
    </Page>
  );
};

export default EditAuthorisedVisitorTemporaryWorker;
